import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { menuService } from '../services/menuService';
import { translate, DEFAULT_UI_LANG } from '../i18n/strings';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const storageKey = (restaurantId) => `lang_${restaurantId}`;

// Açılış dili kuralı: 1) kayıtlı seçim 2) tarayıcı dili 3) İngilizce 4) restoran varsayılanı
const resolveInitialLang = (languages, restaurantId) => {
  const codes = languages.map((l) => l.code);
  if (codes.length === 0) return null;

  try {
    const stored = localStorage.getItem(storageKey(restaurantId));
    if (stored && codes.includes(stored)) return stored;
  } catch (_) { /* localStorage erişilemezse yoksay */ }

  const browser = (navigator.language || navigator.userLanguage || 'en')
    .split('-')[0]
    .toLowerCase();
  if (codes.includes(browser)) return browser;

  if (codes.includes('en')) return 'en';

  const def = languages.find((l) => l.isDefault);
  return def ? def.code : codes[0];
};

export const LanguageProvider = ({ restaurantId, children }) => {
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [currentLang, setCurrentLang] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;

    const init = async () => {
      setReady(false);
      let languages = [];
      try {
        const data = await menuService.getRestaurantLanguages(restaurantId);
        if (Array.isArray(data)) languages = data;
      } catch (error) {
        console.error('Diller yüklenemedi:', error);
      }

      if (!active) return;
      setSupportedLanguages(languages);
      setCurrentLang(resolveInitialLang(languages, restaurantId));
      setReady(true);
    };

    if (restaurantId) {
      init();
    } else {
      setReady(true);
    }

    return () => { active = false; };
  }, [restaurantId]);

  const setLanguage = useCallback((code) => {
    setCurrentLang(code);
    try {
      localStorage.setItem(storageKey(restaurantId), code);
    } catch (_) { /* yoksay */ }
  }, [restaurantId]);

  const t = useCallback(
    (key) => translate(currentLang || DEFAULT_UI_LANG, key),
    [currentLang]
  );

  const value = {
    supportedLanguages,
    currentLang,
    setLanguage,
    t,
    ready,
    // Birden çok dil varsa seçici gösterilir.
    hasMultipleLanguages: supportedLanguages.length > 1,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
